import { useAnalytics } from '@app/services/analytics/AnalyticsContext';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import { Button } from '@shared/components/ui/button';
import { cn } from '@shared/lib/utils';
import { ReactNode } from 'react';
import {
  InsightsExplore,
  useInsightsChartType,
  useInsightsExplore,
  useInsightsTableState,
} from '../hooks/use-insights-filters';

/** Fixed-width leading label so the Explore and Filter rows align. */
export function InsightsControlLabel({ children }: { children: string }) {
  return (
    <Text variant="subtle" className="w-14 shrink-0 text-sm font-medium">
      {children}
    </Text>
  );
}

const OPTIONS: { value: InsightsExplore; label: string; icon: ReactNode }[] = [
  { value: 'overall', label: 'Overall emissions', icon: <Icons.lineChart /> },
  { value: 'site', label: 'By site', icon: <Icons.factory /> },
  { value: 'supplier', label: 'By supplier', icon: <Icons.truck /> },
  { value: 'category', label: 'By category', icon: <Icons.tag /> },
  { value: 'item', label: 'By item', icon: <Icons.scanBarcode /> },
];

/**
 * Grouping selector — a segmented row of pills. Selecting a dimension sets what
 * the breakdown table (and, later, the comparison charts) break down by;
 * "Overall emissions" is the ungrouped view.
 */
export function InsightsExplorePills() {
  const [explore, setExplore] = useInsightsExplore();
  const [, setChartType] = useInsightsChartType();
  const [, setTableState] = useInsightsTableState();
  const { track } = useAnalytics();

  const selectExplore = (value: InsightsExplore) => {
    setExplore(value === 'overall' ? null : value);
    // Snap the chart to each state's default: the two time series for Overall,
    // the scatter comparison for a grouping. A new grouping starts at page 1
    // with its default sort.
    setChartType(value === 'overall' ? null : 'scatter');
    setTableState({ page: null, sort: null });
    track('Explored PG Insights Dimension', { dimension: value });
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <InsightsControlLabel>Explore</InsightsControlLabel>
      {OPTIONS.map((option) => {
        const active = explore === option.value;
        return (
          <Button
            key={option.value}
            variant="outline"
            icon={option.icon}
            className={cn(
              'h-9 rounded-lg font-medium',
              // Matches the period picker's selected-cell treatment, one shade
              // lighter than primary action buttons.
              active
                ? 'border-primary-600 bg-primary-600 font-semibold text-gray-00 shadow-sm hover:bg-primary-600 hover:text-gray-00'
                : 'bg-gray-00 text-gray-700'
            )}
            aria-pressed={active}
            onClick={() => selectExplore(option.value)}
          >
            {option.label}
          </Button>
        );
      })}
    </div>
  );
}
